import { AxiosError } from 'axios';
import { apiClient } from './config';
import { apiVariables } from './apiVariables';
import type {
  CreateWishlistItemRequest,
  CreateWishlistItemResponse,
  UpdateWishlistItemRequest,
  UpdateWishlistItemResponse,
  WishlistLikeResponse,
  WishlistLikesResponse,
  WishlistListResponse,
  WishlistReorderRequest,
  WishlistReorderResponse,
} from '../../types/wishlist';

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof AxiosError) {
    return error.response?.data?.message || error.response?.data?.error || fallback;
  }
  return fallback;
};

/**
 * Get the wishlist, sorted by sortOrder ascending
 */
export const getWishlist = async (): Promise<WishlistListResponse> => {
  try {
    const response = await apiClient.get(apiVariables.WISHLIST);
    const data = response.data;
    return { success: true, message: data?.message, data: data?.data || [] };
  } catch (error) {
    console.error('Error fetching wishlist:', error);
    return { success: false, message: extractErrorMessage(error, 'Failed to fetch wishlist') };
  }
};

/**
 * Submit a new wish
 */
export const submitWish = async (
  wish: CreateWishlistItemRequest
): Promise<CreateWishlistItemResponse> => {
  try {
    const response = await apiClient.post(apiVariables.WISHLIST, wish);
    const data = response.data;
    return { success: true, message: data?.message, data: data?.data };
  } catch (error) {
    console.error('Error submitting wish:', error);
    return { success: false, message: extractErrorMessage(error, 'Failed to submit wish') };
  }
};

/**
 * Update a wishlist item's title/description (own wish, or manager/admin editing any wish)
 */
export const updateWish = async (
  wishlistItemId: number,
  wish: UpdateWishlistItemRequest
): Promise<UpdateWishlistItemResponse> => {
  try {
    const response = await apiClient.put(apiVariables.WISHLIST_UPDATE(wishlistItemId), wish);
    const data = response.data;
    return { success: true, message: data?.message, data: data?.data };
  } catch (error) {
    console.error('Error updating wish:', error);
    return { success: false, message: extractErrorMessage(error, 'Failed to update wish') };
  }
};

/**
 * Toggle like/unlike for a wishlist item
 */
export const toggleWishlistLike = async (wishlistItemId: number): Promise<WishlistLikeResponse> => {
  try {
    const response = await apiClient.post(apiVariables.WISHLIST_LIKE(wishlistItemId));
    const data = response.data;
    return { success: true, message: data?.message, data: data?.data };
  } catch (error) {
    console.error('Error toggling wishlist like:', error);
    return { success: false, message: extractErrorMessage(error, 'Failed to update like') };
  }
};

/**
 * Get the list of users who liked a wishlist item
 */
export const getWishlistLikes = async (wishlistItemId: number): Promise<WishlistLikesResponse> => {
  try {
    const response = await apiClient.get(apiVariables.WISHLIST_LIKES(wishlistItemId));
    const data = response.data;
    return { success: true, message: data?.message, data: data?.data || [] };
  } catch (error) {
    console.error('Error fetching wishlist likes:', error);
    return { success: false, message: extractErrorMessage(error, 'Failed to fetch likes') };
  }
};

/**
 * Save reordered wishlist (manager-only)
 */
export const reorderWishlist = async (
  request: WishlistReorderRequest
): Promise<WishlistReorderResponse> => {
  try {
    const response = await apiClient.put(apiVariables.WISHLIST_REORDER, request);
    const data = response.data;
    return { success: true, message: data?.message };
  } catch (error) {
    console.error('Error saving wishlist order:', error);
    return { success: false, message: extractErrorMessage(error, 'Failed to save wishlist order') };
  }
};
